/**
 * Identity API — recipient resolution over the canonical Identity Service (backend lib/identity).
 * Resolves the people the practice communicates with (solicitors, clients, opponents, experts,
 * courts, internal barristers/clerks) so Compose can offer an autocomplete instead of raw typing.
 *
 * This is the CONSUMER side: it renders what the backend resolves and never re-implements matching.
 */
import { api } from './api';

export type IdentityKind = 'contact' | 'barrister' | 'clerk' | 'court' | 'organisation';
export type QueryType = 'email' | 'phone' | 'matter' | 'name';

/** Why an identity matched — shown so the user can disambiguate several candidates. */
export type MatchReason =
  | { kind: 'email'; value: string }
  | { kind: 'phone'; value: string }
  | { kind: 'name' }
  | { kind: 'organisation'; value: string }
  | { kind: 'matter'; matterNumber: string };

/** A resolved identity, unified across kinds. Lean mirror of the backend Identity contract. */
export interface Identity {
  ref: { kind: IdentityKind; id: string };
  displayName: string;
  subtitle: string | null;
  organisation: string | null;
  emails: string[];
  phones: string[];
  relationship: string | null;
}

export interface IdentityMatch {
  identity: Identity;
  matchedOn: MatchReason;
}

/** GET /identity/search?q= — access-scoped resolution. Empty query → no request. */
export async function searchIdentities(q: string): Promise<IdentityMatch[]> {
  const query = q.trim();
  if (!query) return [];
  const res = await api.get<{ results: IdentityMatch[] }>('/identity/search', { params: { q: query } });
  return res.data.results;
}

export interface IdentitySearchTelemetry {
  query: string;
  queryType: QueryType;
  resultCount: number;
  selectedKind?: IdentityKind | null;
  selectedPosition?: number | null;
  noResult?: boolean;
  createNew?: boolean;
  timeToSelectionMs?: number | null;
}

/**
 * POST /identity/telemetry — fire-and-forget usage signal. NEVER blocks the compose flow and
 * NEVER surfaces an error to the user: it is analytics, so a failure is silently swallowed.
 */
export function recordIdentitySearchTelemetry(payload: IdentitySearchTelemetry): void {
  void api.post('/identity/telemetry', payload).catch(() => { /* best effort — analytics only */ });
}

/**
 * Lean client-side query classifier — ONLY to label telemetry (queryType). It mirrors the backend
 * classify() intent buckets but is deliberately not authoritative: the backend re-classifies for
 * the actual resolution. Kept trivial on purpose.
 */
export function classifyQueryType(raw: string): QueryType {
  const q = raw.trim();
  if (q.includes('@')) return 'email';
  // A matter reference looks like MAT-2026-001 / CR-1234 — letters, then digits with separators.
  if (/^[A-Za-z]{2,}[-/ ]?\d/.test(q) && /\d/.test(q)) return 'matter';
  // Mostly digits / phone punctuation → phone.
  const digits = q.replace(/[^\d]/g, '');
  if (digits.length >= 6 && /^[\d\s+()./-]+$/.test(q)) return 'phone';
  return 'name';
}
